'use client';

import { useEffect, useRef, useState } from 'react';
import type { BarcodeConfig, ReceiptSettings } from '@/lib/templates/schema';
import { SectionDivider } from './SectionDivider';

interface BarcodeSectionProps {
  config: BarcodeConfig;
  settings: ReceiptSettings;
}

// Extract user-friendly error message from JsBarcode error
function getErrorMessage(error: unknown, type: string): string {
  const errorStr = String(error);
  
  if (errorStr.includes('not a valid input')) {
    switch (type) {
      case 'ean13':
        return 'Invalid EAN-13: requires exactly 12-13 digits with valid checksum';
      case 'upc':
        return 'Invalid UPC: requires exactly 11-12 digits with valid checksum';
      case 'code39':
        return 'Invalid Code 39: only 0-9, A-Z, and - . $ / + % space allowed';
      default:
        return 'Invalid barcode value';
    }
  }
  
  return 'Unable to generate barcode';
}

// Get QR code length based on detail level
function getQRLength(value: string): number {
  switch (value) {
    case 'QR_SIMPLE':
      return 8;
    case 'QR_MEDIUM':
      return 16;
    case 'QR_DETAILED':
      return 32;
    default:
      return 16;
  }
}

// Generate a random alphanumeric string for QR codes
function generateRandomQRValue(length: number = 16): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function BarcodeSection({ config, settings }: BarcodeSectionProps) {
  const barcodeRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Use ref to store QR values to avoid re-render loops
  const qrValuesRef = useRef<Record<string, string>>({});

  // Get or generate QR value for the current detail level
  const getQrValue = (detailLevel: string): string => {
    if (qrValuesRef.current[detailLevel]) {
      return qrValuesRef.current[detailLevel];
    }
    const length = getQRLength(detailLevel);
    const newValue = generateRandomQRValue(length);
    qrValuesRef.current[detailLevel] = newValue;
    return newValue;
  };

  const alignmentClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[config.alignment];
  const isNarrow = settings.spacing === 'narrow';

  useEffect(() => {
    let isCancelled = false;
    
    const renderBarcode = async () => {
      if (typeof window === 'undefined' || !barcodeRef.current) return;

      // Clear previous content and error
      barcodeRef.current.innerHTML = '';
      setError(null);

      try {
        // Handle QR code separately using qrcode library
        if (config.type === 'qr') {
          const QRCode = (await import('qrcode')).default;
          
          // Check if cancelled before continuing (React Strict Mode runs effects twice)
          if (isCancelled || !barcodeRef.current) return;
          
          // Determine detail level from config value
          const detailLevel = config.value?.startsWith('QR_') ? config.value : 'QR_MEDIUM';
          const valueToEncode = getQrValue(detailLevel);
          
          // Create canvas for QR code
          const canvas = document.createElement('canvas');
          await QRCode.toCanvas(canvas, valueToEncode, {
            width: config.height, // Use height as size for square QR
            margin: 0,
            color: {
              dark: settings.textColor,
              light: '#00000000', // Transparent background
            },
          });
          
          // Check again before appending
          if (isCancelled || !barcodeRef.current) return;
          barcodeRef.current.innerHTML = ''; // Clear again to prevent duplicates
          barcodeRef.current.appendChild(canvas);
          return;
        }

        // Handle regular barcodes with JsBarcode
        const JsBarcode = (await import('jsbarcode')).default;
        
        // Check if cancelled before continuing
        if (isCancelled || !barcodeRef.current) return;
        
        // Create SVG element
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        barcodeRef.current.innerHTML = ''; // Clear again to prevent duplicates
        barcodeRef.current.appendChild(svg);

        // Map our barcode types to JsBarcode formats
        const formatMap: Record<string, string> = {
          code128: 'CODE128',
          code39: 'CODE39',
          ean13: 'EAN13',
          upc: 'UPC',
        };

        JsBarcode(svg, config.value, {
          format: formatMap[config.type] || 'CODE128',
          width: config.width,
          height: config.height,
          displayValue: false, // We render text separately as HTML for better export support
          font: settings.font,
          fontSize: settings.fontSize,
          textMargin: 0,
          margin: 0,
          background: 'transparent',
          lineColor: settings.textColor,
        });
      } catch (err) {
        if (isCancelled) return;
        const errorMessage = getErrorMessage(err, config.type);
        setError(errorMessage);
        if (barcodeRef.current) {
          barcodeRef.current.innerHTML = '';
        }
      }
    };

    renderBarcode();
    
    // Cleanup function for React Strict Mode
    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, settings]);

  return (
    <div className={`receipt-section ${isNarrow ? 'py-0.5' : 'py-2'}`}>
      <div className={`flex flex-col items-center ${alignmentClass}`}>
        <div ref={barcodeRef} className="barcode-container" />
        {/* Render barcode value as HTML text for reliable export (html2canvas has SVG text issues) */}
        {/* Never show value for QR codes */}
        {config.showValue && !error && config.type !== 'qr' && (
          <div
            style={{
              fontFamily: settings.font,
              fontSize: settings.fontSize,
              color: settings.textColor,
              textAlign: 'center',
              marginTop: '2px',
            }}
          >
            {config.value}
          </div>
        )}
      </div>

      {/* Error message displayed on receipt */}
      {error && (
        <div 
          className={`flex ${alignmentClass}`}
          style={{ 
            fontSize: settings.fontSize * 0.9,
            color: settings.textColor,
            opacity: 0.7,
            padding: '4px 0',
          }}
        >
          <div style={{ 
            textAlign: 'center',
            border: `1px dashed ${settings.textColor}`,
            padding: '8px 12px',
            borderRadius: '2px',
          }}>
            ⚠ {error}
          </div>
        </div>
      )}

      {/* Divider */}
      {config.showDivider && (
        <SectionDivider style={config.dividerStyle} settings={settings} />
      )}
    </div>
  );
}



